<template>
  <v-card :loading="loading.details">
    <!-- Header con color según estado -->
    <v-sheet :color="getLogColor(incident?.incidentStatus)" dark class="px-4 py-3 d-flex align-center">
      <v-icon left>mdi-alert-circle</v-icon>
      <span class="title font-weight-bold">{{ $t('IncidentCard.Title') }} #{{ incidentId }}</span>
      <v-spacer></v-spacer>
      <v-chip label small color="white" :text-color="getLogColor(incident?.incidentStatus)" class="font-weight-bold">
        {{ getStatusName(incident?.incidentStatus) }}
      </v-chip>
    </v-sheet>

    <v-card-text class="pt-4">

      <!-- Sección: Información General -->
      <div class="overline grey--text mb-2">{{ $t('IncidentCard.GeneralInfo') }}</div>
      <v-row dense>
        <v-col cols="6">
          <div class="info-field">
            <v-icon x-small class="mr-1 grey--text">mdi-calendar</v-icon>
            <span class="caption grey--text">{{ $t('IncidentCard.CreationDate') }}</span>
            <div class="body-2 font-weight-medium">{{ formatDate(incident?.creationDate) || '-' }}</div>
          </div>
        </v-col>
        <v-col cols="6">
          <div class="info-field">
            <v-icon x-small class="mr-1 grey--text">mdi-domain</v-icon>
            <span class="caption grey--text">{{ $t('IncidentCard.VendorCode') }}</span>
            <div class="body-2 font-weight-medium">{{ incident?.vendorCode || '-' }}</div>
          </div>
        </v-col>
        <v-col cols="6">
          <div class="info-field">
            <v-icon x-small class="mr-1 grey--text">mdi-file-document</v-icon>
            <span class="caption grey--text">{{ $t('IncidentCard.Invoice') }}</span>
            <div class="body-2 font-weight-medium">{{ incident?.invoice || '-' }}</div>
          </div>
        </v-col>
        <v-col cols="6">
          <div class="info-field">
            <v-icon x-small class="mr-1 grey--text">mdi-factory</v-icon>
            <span class="caption grey--text">{{ $t('IncidentCard.Facility') }}</span>
            <div class="body-2 font-weight-medium">{{ incident?.facility || '-' }}</div>
          </div>
        </v-col>
        <v-col cols="6">
          <div class="info-field">
            <v-icon x-small class="mr-1 grey--text">mdi-account-group</v-icon>
            <span class="caption grey--text">{{ $t('IncidentCard.Department') }}</span>
            <div class="body-2 font-weight-medium">{{ getDepartmentName(incident?.idDepartment) }}</div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <!-- Sección: Clasificación -->
      <div class="overline grey--text mb-2">{{ $t('IncidentCard.Classification') }}</div>
      <v-row dense>
        <v-col cols="6">
          <div class="info-field">
            <span class="caption grey--text">{{ $t('IncidentCard.Incident') }}</span>
            <div><v-chip small label color="red lighten-4" text-color="red darken-3">{{ incident?.incidentName || '-' }}</v-chip></div>
          </div>
        </v-col>
        <v-col cols="6">
          <div class="info-field">
            <span class="caption grey--text">{{ $t('IncidentCard.Category') }}</span>
            <div><v-chip small label color="orange lighten-4" text-color="orange darken-3">{{ incident?.incidentCategory || '-' }}</v-chip></div>
          </div>
        </v-col>
        <v-col cols="6" v-if="incident?.incidentType">
          <div class="info-field">
            <span class="caption grey--text">{{ $t('IncidentCard.Type') }}</span>
            <div class="body-2 font-weight-medium">{{ incident?.incidentType }}</div>
          </div>
        </v-col>
        <v-col cols="6" v-if="incident?.closeIncidentType">
          <div class="info-field">
            <span class="caption grey--text">{{ $t('IncidentCard.CloseType') }}</span>
            <div><v-chip small label color="green lighten-4" text-color="green darken-3">{{ incident?.closeIncidentType }}</v-chip></div>
          </div>
        </v-col>
      </v-row>

      <!-- Planner Actions -->
      <template v-if="incident?.plannerActions">
        <v-divider class="my-3"></v-divider>
        <div class="overline grey--text mb-2">{{ $t('IncidentCard.PlannerActions') }}</div>
        <v-sheet rounded color="blue-grey lighten-5" class="pa-3">
          <v-icon small class="mr-1 blue-grey--text">mdi-text</v-icon>
          <span class="body-2">{{ incident?.plannerActions }}</span>
        </v-sheet>
      </template>

      <!-- Cancellation Reason -->
      <template v-if="incident?.cancellationReason">
        <v-divider class="my-3"></v-divider>
        <div class="overline grey--text mb-2">{{ $t('IncidentCard.CancellationReason') }}</div>
        <v-sheet rounded color="red lighten-5" class="pa-3">
          <v-icon small class="mr-1 red--text">mdi-cancel</v-icon>
          <span class="body-2">{{ incident?.cancellationReason }}</span>
        </v-sheet>
      </template>

      <!-- Parts involved -->
      <template v-if="incident.details && incident.details.length > 0">
        <v-divider class="my-3"></v-divider>
        <div class="overline grey--text mb-2">{{ $t('IncidentCard.PartsInvolved') }}</div>
        <v-data-table
          :items="incident.details"
          :headers="headers"
          hide-default-footer
          disable-pagination
          dense
          class="elevation-0 rounded"
          style="border: 1px solid #e0e0e0"
        ></v-data-table>
      </template>

      <!-- Archivos adjuntos -->
      <template v-if="attachedFiles && attachedFiles.length > 0">
        <v-divider class="my-3"></v-divider>
        <div class="overline grey--text mb-2">
          <v-icon small class="mr-1">mdi-paperclip</v-icon>{{ $t('IncidentCard.AttachedFiles') }}
        </div>
        <v-list dense class="pa-0">
          <v-list-item
            v-for="file in attachedFiles"
            :key="file.index"
            @click="downloadFile(file.index, file.name)"
            class="px-2 rounded mb-1"
            style="border: 1px solid #e0e0e0; cursor: pointer"
          >
            <v-list-item-icon class="mr-2 my-auto">
              <v-icon small color="primary">mdi-file-download</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="body-2">{{ file.name }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon x-small color="primary">
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </template>

      <!-- Change History -->
      <template v-if="incident.logs && incident.logs.length > 0">
        <v-divider class="my-3"></v-divider>
        <div class="overline grey--text mb-2">
          <v-icon small class="mr-1">mdi-history</v-icon>{{ $t('IncidentCard.ChangeHistory') }}
        </div>
        <v-timeline dense align-top class="mt-1">
          <v-timeline-item
            v-for="log in incident.logs"
            :key="log.id"
            :color="getLogColor(log.newStatus)"
            small
          >
            <span class="caption grey--text">{{ formatDate(log.updatedAt) }}</span>
            <span class="caption font-weight-medium ml-1">{{ log.updatedBy }}</span>
            <div class="mt-1">
              <span v-if="log.oldStatus">
                <v-chip x-small label :color="getLogColor(log.oldStatus)" text-color="white">{{ getStatusName(log.oldStatus) }}</v-chip>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
              </span>
              <v-chip x-small label :color="getLogColor(log.newStatus)" text-color="white">{{ getStatusName(log.newStatus) }}</v-chip>
              <div v-if="log.idDepartmentOld && log.idDepartmentNew && log.idDepartmentOld !== log.idDepartmentNew" class="caption mt-1 grey--text">
                {{ $t('IncidentCard.Dept') }}: {{ getDepartmentName(log.idDepartmentOld) }} → {{ getDepartmentName(log.idDepartmentNew) }}
              </div>
              <div v-if="log.supportRequestedTo" class="caption mt-1 orange--text">
                <v-icon x-small color="orange">mdi-account-arrow-right</v-icon>
                {{ $t('IncidentCard.SupportRequestedTo') }}: {{ log.supportRequestedTo }}
              </div>
            </div>
          </v-timeline-item>
        </v-timeline>
      </template>

    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions class="pa-3">
      <v-spacer></v-spacer>
      <v-btn small depressed @click="$emit('close')">{{ $t('IncidentCard.Close') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { showMessage } from '../../../mixins'
export default {
  name: 'IncidentDetailCard',
  props: {
    incidentId: Number
  },
  mixins: [showMessage],
  data: () => ({
    incident: {
      details: []
    },
    attachedFiles: [],
    departments: [],
    loading: {
      details: false
    }
  }),
  computed: {
    headers () {
      return [
        { text: this.$t('IncidentCard.PartNumber'), value: 'partNumber', align: 'center', sortable: true, filterable: true },
        { text: this.$t('IncidentCard.Description'), value: 'description', align: 'center', sortable: true, filterable: true },
        { text: this.$t('IncidentCard.DocumentType'), value: 'documentType', align: 'center', sortable: true, filterable: true },
        { text: this.$t('IncidentCard.Quantity'), value: 'quantity', align: 'center', sortable: true, filterable: true }
      ]
    }
  },
  methods: {
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '-'
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}`
    },
    getDepartmentName (id) {
      if (!id && id !== 0) return '-'
      const dept = this.departments.find(d => d.id === id || String(d.code) === String(id))
      return dept ? dept.name : String(id)
    },
    getLogColor (status) {
      if (status === 1) return 'orange'
      if (status === 2) return '#66BB6A'
      if (status === 3) return 'grey'
      return 'blue'
    },
    getStatusName (status) {
      if (status === 1) return 'Open'
      if (status === 2) return 'Resolved'
      if (status === 3) return 'Cancelled'
      return String(status)
    },
    async getDepartments () {
      try {
        const res = await this.$proxy.get('api/users/departments')
        this.departments = res.data
      } catch (error) {
        // non-critical
      }
    },
    async getIncidentDetail () {
      try {
        this.loading.details = true
        const res = await this.$proxy.get(`api/incidents/${this.incidentId}`)
        this.incident = res.data
        await this.getIncidentFiles()
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.details = false
      }
    },
    async getIncidentFiles () {
      try {
        const res = await this.$proxy.get(`api/incidents/${this.incidentId}/files`)
        this.attachedFiles = res.data.files || []
      } catch (error) {
        this.attachedFiles = []
      }
    },
    async downloadFile (fileIndex, fileName) {
      try {
        this.loading.details = true
        const response = await this.$proxy.getDownload(`api/incidents/${this.incidentId}/files/${fileIndex}`)

        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showHttpErrorMessage(error)
      } finally {
        this.loading.details = false
      }
    }
  },
  watch: {
    incidentId: {
      handler (val) {
        if (val) {
          this.getIncidentDetail()
        }
      },
      immediate: true
    }
  },
  created () {
    this.getDepartments()
  }
}
</script>

<style scoped>
.info-field {
  padding: 6px 8px;
  border-radius: 6px;
  background: #f5f5f5;
  min-height: 52px;
}
.info-field .caption {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
